import { useState } from 'react';

import { Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import type { FileRejection, FileWithPath } from '@mantine/dropzone';

import {
  IconAsterisk,
  IconInfoSquareRounded,
  IconPaperclip,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import type { FileWithPathAndType } from '../../../shared/api/magellan/bidResponses/dto';
import type { BidResponseDocumentType } from '../../../shared/entities/BidResponseDocument';
import type { WorkspaceDocumentType } from '../../../shared/entities/WorkspaceDocuments';

type DocumentInputProps<
  T extends BidResponseDocumentType | WorkspaceDocumentType,
> = {
  error?: string | null;
  onDrop: (files: FileWithPathAndType<T>[]) => void;
  label?: string;
  subtitle?: string;
  required?: boolean;
  documentsPreviewComponent?: React.ReactNode;
  dropzoneCaption?: React.ReactNode;
};

export function DocumentInput<
  T extends BidResponseDocumentType | WorkspaceDocumentType,
>({
  error,
  onDrop,
  label,
  subtitle,
  documentsPreviewComponent,
  dropzoneCaption,
  required = false,
}: DocumentInputProps<T>) {
  const { t } = useTranslation('documents');
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);

  const handleDrop = (newFiles: FileWithPath[]) => {
    const filesWithType = newFiles.map(file => ({
      file,
      type: null,
    }));

    onDrop(filesWithType);
  };

  return (
    <Stack spacing="03">
      <Stack spacing="01">
        <Group noWrap spacing="02">
          <Text weight={500} size="sm" lh="20px">
            {label}
          </Text>
          {required && (
            <ThemeIcon
              radius="100%"
              size="03"
              variant="light"
              sx={theme => ({
                backgroundColor: theme.colors.primary[1],
                color: theme.colors.primary[6],
              })}
            >
              <IconAsterisk size={8} stroke={3} />
            </ThemeIcon>
          )}
        </Group>
        <Text variant="xs" weight={400} c="gray.5">
          {subtitle}
        </Text>
      </Stack>

      {documentsPreviewComponent}

      <DropzoneComponent
        onDrop={handleDrop}
        onReject={setRejectedFiles}
        dropzoneCaption={dropzoneCaption}
        error={error}
      />

      {error && (
        <Text weight={500} size="xs" c="red.5" lh="20px" mt={-4}>
          {error}
        </Text>
      )}

      {rejectedFiles.length > 0 &&
        rejectedFiles.map((file, index) => (
          <Text key={index} weight={500} variant="sm" c="red.5">
            {file.file.name} - {t('dropZone.fileTypeNotSupported')}
          </Text>
        ))}
    </Stack>
  );
}

export function BlueHighlight({ children }: React.HTMLProps<HTMLSpanElement>) {
  return (
    <Text component="span" variant="sm" fw="500" c="primary.6">
      {children}
    </Text>
  );
}

function DropzoneComponent<
  T extends BidResponseDocumentType | WorkspaceDocumentType,
>({
  onDrop,
  dropzoneCaption,
  onReject,
  error,
}: {
  onDrop: (files: FileWithPath[]) => void;
  onReject: (files: FileRejection[]) => void;
  dropzoneCaption?: DocumentInputProps<T>['dropzoneCaption'];
  error?: DocumentInputProps<T>['error'];
}) {
  const { t } = useTranslation('documents');

  return (
    <Dropzone
      px="05"
      py="04"
      onReject={onReject}
      onDrop={onDrop}
      accept={{
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          ['.docx'],
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
          '.xlsx',
        ],
        'application/vnd.ms-powerpoint': ['.ppt'],
        'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          ['.pptx'],
      }}
      sx={theme => ({
        borderColor: error ? theme.colors.red[5] : theme.colors.gray[3],
        borderRadius: theme.radius.md,
      })}
    >
      <Stack spacing="01" w="100%">
        <Group noWrap spacing="02" c="gray.4" m="auto">
          <IconPaperclip />
          {dropzoneCaption}
          <IconInfoSquareRounded />
        </Group>
        <Text variant="xs" fw="400" c="gray.4" m="auto">
          {t('dropZone.supportedFileTypes')}
        </Text>
      </Stack>
    </Dropzone>
  );
}
